/**
 * useFlagsCookie - A hook to read a flag from the flags cookie.
 *
 * This hooks will return undefined on the server, on rehydration, and if the
 * flags cookie is not present or does not contain the flag specified by the
 * `name` parameter.
 */
import {useMemo} from "react";

import {parsePasses} from "./pass-cookies.ts";
import {useCookies} from "./use-cookies.ts";

export type FlagResult =
    | {status: "loading"}
    | {status: "success"; value: boolean | undefined};

export const useFlagsCookie = (name: string): FlagResult => {
    const Cookies = useCookies();

    return useMemo(() => {
        if (Cookies) {
            // NOTE(kevinb): The reason we don't use `getPass(name)` here is that
            // it isn't SSR safe while `useCookies()` is.
            const cookie = Cookies.readCookie("flags") || "";
            parsePasses(decodeURIComponent(cookie)); // ?
            const value = parsePasses(decodeURIComponent(cookie))[name];
            return {status: "success", value: value};
        }

        return {status: "loading"};
    }, [Cookies, name]);
};

export const toBooleanOrUndefined = (
    result: FlagResult,
): boolean | undefined => {
    if (result.status === "success") {
        return result.value;
    }
    return undefined;
};
